import React, {useCallback, useEffect, useMemo, useState} from "react";
import {AnimatePresence, motion} from 'framer-motion';
import axios from "axios";
import {
    Button,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Input,
} from "@material-tailwind/react";
import {
    ArrowPathIcon,
    BoltIcon,
    CheckCircleIcon,
    ClockIcon,
    MagnifyingGlassIcon,
    ExclamationTriangleIcon,
    StopCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {
    AUTOMATION_REASON_LABELS,
} from "./automationEligibility";
import AutomationItemSelector, {formatAutomationAvailableAt} from './AutomationItemSelector';
import BatchAutomationHistory from './BatchAutomationHistory';
import BatchAutomationResults from './BatchAutomationResults';

const TERMINAL = ['COMPLETED', 'PARTIALLY_COMPLETED', 'FAILED', 'CANCELLED'];

const label = (value) => (value || '')
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const validDelay = (value) => /^\d{1,5}$/.test(value)
    && Number(value) >= 0
    && Number(value) <= 86400;

export default function BatchAutomationDialog({
    open,
    onClose,
    apiUrl,
    requestHeaders,
    onOrdersRefresh,
}) {
    const [preview, setPreview] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedItemsByOrder, setSelectedItemsByOrder] = useState({});
    const [search, setSearch] = useState('');
    const [itemDelay, setItemDelay] = useState('15');
    const [profileDelay, setProfileDelay] = useState('30');
    const [batch, setBatch] = useState(null);
    const [latestBatch, setLatestBatch] = useState(null);
    const [agent, setAgent] = useState(null);
    const [view, setView] = useState('new');
    const [historyPage, setHistoryPage] = useState(0);
    const [detailId, setDetailId] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const selectable = useMemo(() => preview.filter(order => (
        order.profileNumber >= 1
        && order.profileNumber <= 99
        && !order.activeJob
        && Boolean(order.orderNumber?.trim())
        && (order.items || []).some(item => item.eligible)
    )), [preview]);
    const isTerminal = batch ? TERMINAL.includes(batch.status) : false;
    const showStart = view === 'new';
    const selectedOrders = preview.filter(order => selectedIds.includes(order.orderId));
    const selectedItemCount = selectedOrders.reduce((count, order) => count + (selectedItemsByOrder[order.orderId] || []).length, 0);
    const incompleteProfiles = selectedOrders.filter(order => !(selectedItemsByOrder[order.orderId] || []).length);
    const canStart = selectedOrders.length > 0 && incompleteProfiles.length === 0
        && validDelay(itemDelay) && validDelay(profileDelay) && !loading;
    const visibleOrders = preview.filter(order => (
        `${order.email} ${order.profileNumber} ${order.orderNumber} ${(order.items || []).map(item => item.title).join(' ')}`
            .toLowerCase().includes(search.trim().toLowerCase())
    ));

    const load = useCallback(async () => {
        const [previewResponse, batchResponse, agentResponse] = await Promise.all([
            axios.get(`${apiUrl}/automation/batches/preview`, {headers: requestHeaders}),
            axios.get(`${apiUrl}/automation/batches/latest`, {headers: requestHeaders}),
            axios.get(`${apiUrl}/automation/agents/status`, {headers: requestHeaders}),
        ]);
        const orders = previewResponse.data?.orders || [];
        setPreview(orders);
        setLatestBatch(batchResponse.status === 204 ? null : batchResponse.data);
        setAgent(agentResponse.status === 204 ? null : agentResponse.data);
        setSelectedIds(current => current.filter(id => orders.some(order => (
            order.orderId === id
            && order.profileNumber >= 1
            && order.profileNumber <= 99
            && !order.activeJob
            && Boolean(order.orderNumber?.trim())
            && (order.items || []).some(item => item.eligible)
        ))));
        setSelectedItemsByOrder(current => Object.fromEntries(orders.map(order => [order.orderId,
            (current[order.orderId] || []).filter(id => !order.activeJob
                && (order.items || []).some(item => item.itemId === id && item.eligible)),
        ])));
    }, [apiUrl, requestHeaders]);

    useEffect(() => {
        if (!open) return undefined;
        setLoading(true);
        setError('');
        setView('new');
        setHistoryPage(0);
        setBatch(null);
        setDetailId(null);
        setLatestBatch(null);
        setItemDelay('15');
        setProfileDelay('30');
        setSelectedIds([]);
        setSelectedItemsByOrder({});
        setSearch('');
        load()
            .catch(requestError => setError(
                requestError.response?.data?.error || 'Could not load batch automation preview.'
            ))
            .finally(() => setLoading(false));
        return undefined;
    }, [load, open]);

    useEffect(() => {
        if (!open || view !== 'batch' || detailId == null) return undefined;
        let ignore = false;
        setDetailLoading(true);
        setBatch(null);
        setError('');
        axios.get(`${apiUrl}/automation/batches/${detailId}`, {headers: requestHeaders})
            .then(response => {if (!ignore) setBatch(response.data);})
            .catch(requestError => {if (!ignore) setError(requestError.response?.data?.error || 'Could not load this batch.');})
            .finally(() => {if (!ignore) setDetailLoading(false);});
        return () => {ignore = true;};
    }, [apiUrl, requestHeaders, open, view, detailId]);

    useEffect(() => {
        if (!open || !batch?.batchId || view !== 'batch' || TERMINAL.includes(batch.status)) return undefined;
        let ignore = false;
        const timer = setInterval(async () => {
            try {
                const response = await axios.get(
                    `${apiUrl}/automation/batches/${batch.batchId}`,
                    {headers: requestHeaders}
                );
                if (ignore) return;
                setBatch(response.data);
                setLatestBatch(current => current?.batchId === response.data.batchId ? response.data : current);
                if ((response.data.jobs || []).some(job => (
                    (job.items || []).some(item => item.referenceNumber)
                ))) {
                    onOrdersRefresh?.();
                }
            } catch (requestError) {
                console.error('Error polling batch automation:', requestError);
            }
        }, 2500);
        return () => {ignore = true; clearInterval(timer);};
    }, [apiUrl, batch?.batchId, batch?.status, onOrdersRefresh, open, requestHeaders, view]);

    const openBatch = id => {
        setError('');
        setDetailId(id);
        setView('batch');
    };

    const newBatch = () => {
        setError('');
        setView('new');
        setLoading(true);
        load().catch(requestError => setError(requestError.response?.data?.error || 'Could not refresh preview.'))
            .finally(() => setLoading(false));
    };

    const toggleOrder = (orderId) => {
        setSelectedItemsByOrder(current => ({...current, [orderId]: []}));
        setSelectedIds(current => current.includes(orderId)
            ? current.filter(id => id !== orderId)
            : [...current, orderId]);
    };

    const selectAll = () => {
        const all = selectable.map(order => order.orderId);
        const clear = all.every(id => selectedIds.includes(id));
        setSelectedIds(clear ? [] : all);
        if (clear) setSelectedItemsByOrder({});
    };

    const start = async () => {
        if (!canStart) return;
        setLoading(true);
        setError('');
        try {
            const response = await axios.post(`${apiUrl}/automation/batches`, {
                orderIds: preview
                    .filter(order => selectedIds.includes(order.orderId))
                    .map(order => order.orderId),
                itemIdsByOrder: Object.fromEntries(selectedOrders.map(order => [
                    order.orderId, selectedItemsByOrder[order.orderId],
                ])),
                interItemDelaySeconds: Number(itemDelay),
                interProfileDelaySeconds: Number(profileDelay),
            }, {headers: requestHeaders});
            setLatestBatch(response.data);
            setSelectedIds([]);
            setSelectedItemsByOrder({});
            openBatch(response.data.batchId);
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Could not start batch automation.');
        } finally {
            setLoading(false);
        }
    };

    const cancel = async () => {
        if (!batch?.batchId) return;
        setLoading(true);
        try {
            const response = await axios.post(
                `${apiUrl}/automation/batches/${batch.batchId}/cancel`,
                {},
                {headers: requestHeaders}
            );
            setBatch(response.data);
            setLatestBatch(current => current?.batchId === response.data.batchId ? response.data : current);
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Could not cancel batch automation.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={Boolean(open)}
            handler={onClose}
            size="xl"
            className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden dark:bg-dark-900"
        >
            <DialogHeader className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-5 dark:border-dark-700 dark:text-gray-100">
                <div>
                    <span className="flex items-center gap-2">
                        <BoltIcon className="h-5 w-5 text-cyan-800 dark:text-panda-cyan" />
                        Process batch automation
                    </span>
                    <p className="mt-1 text-sm font-normal text-gray-500 dark:text-gray-400">{showStart ? 'Choose profiles, then choose the items to run.' : view === 'history' ? 'Browse previous runs and their results.' : 'Profile results, references and execution times.'}</p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                {view !== 'new' && <button type="button" onClick={newBatch} disabled={loading} className="mv-secondary-button"><BoltIcon className="h-4 w-4" /> New batch</button>}
                <button type="button" onClick={() => {setError(''); setView('history');}} disabled={loading || view === 'history'} className="mv-secondary-button"><ClockIcon className="h-4 w-4" /> Batch history</button>
                <button type="button" aria-label="Close batch automation" onClick={onClose} className="rounded-md p-1.5 hover:bg-gray-100 dark:hover:bg-dark-800">
                    <XMarkIcon className="h-5 w-5" />
                </button>
                </div>
            </DialogHeader>

            <DialogBody className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-6 py-4">
                <div className="min-w-0 space-y-4">
                    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 ${
                        agent?.online
                            ? 'border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-500/10'
                            : 'border-amber-200 bg-amber-50 dark:border-amber-400/30 dark:bg-amber-400/10'
                    }`}>
                        <div><p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                            Local agent: {agent?.online ? 'Online' : 'Offline'}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                            {agent?.machineName || 'The batch will wait for the local agent.'}
                        </p></div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Purchase: 3 days · Case: 16 hours · Replacement approval: 6 days</p>
                    </div>

                    {error && (
                        <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-panda-pink/30 dark:bg-panda-pink/10 dark:text-panda-pink">
                            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                            {error}
                        </div>
                    )}

                    {showStart ? (
                        <>
                            {latestBatch && !TERMINAL.includes(latestBatch.status) && <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyan-200 bg-cyan-50 p-4 dark:border-panda-cyan/30 dark:bg-panda-cyan/10">
                                <div><p className="text-sm font-semibold text-cyan-900 dark:text-panda-cyan">Batch #{latestBatch.batchId} is {label(latestBatch.status).toLowerCase()}</p>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Profiles already running are unavailable for a new batch.</p></div>
                                <button type="button" onClick={() => openBatch(latestBatch.batchId)} className="mv-secondary-button">View current batch</button>
                            </div>}
                            <details className="rounded-xl border border-gray-200 px-4 py-3 dark:border-dark-700">
                                <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">Timing settings <span className="font-normal text-gray-500">· {itemDelay}s between items · {profileDelay}s between profiles</span></summary>
                            <div className="mt-4 grid min-w-0 gap-3 sm:grid-cols-2">
                                <Input
                                    label="Delay between items (seconds)"
                                    type="number"
                                    min="0"
                                    max="86400"
                                    value={itemDelay}
                                    onChange={event => setItemDelay(event.target.value)}
                                    error={!validDelay(itemDelay)}
                                />
                                <Input
                                    label="Delay between profiles (seconds)"
                                    type="number"
                                    min="0"
                                    max="86400"
                                    value={profileDelay}
                                    onChange={event => setProfileDelay(event.target.value)}
                                    error={!validDelay(profileDelay)}
                                />
                            </div>
                            </details>

                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    Profiles <span className="font-normal text-gray-500">· {selectedIds.length}/{selectable.length} selected · {selectedItemCount} {selectedItemCount === 1 ? 'item' : 'items'}</span>
                                </p>
                                <div className="flex items-center gap-2"><button type="button" disabled={loading} onClick={() => {
                                    setLoading(true);
                                    setError('');
                                    load().catch(requestError => setError(requestError.response?.data?.error || 'Could not refresh preview.'))
                                        .finally(() => setLoading(false));
                                }} className="mv-secondary-button"><ArrowPathIcon className="h-4 w-4" /> Refresh</button>
                                <button type="button" disabled={loading || !selectable.length} onClick={selectAll} className="mv-secondary-button">
                                    <CheckCircleIcon className="h-4 w-4" />
                                    {selectedIds.length === selectable.length && selectable.length ? 'Clear profiles' : 'Select all profiles'}
                                </button></div>
                            </div>

                            <div className="relative"><MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                <input type="search" value={search} onChange={event => setSearch(event.target.value)} aria-label="Search profiles or items"
                                    placeholder="Search profile, email, order or item…" className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-cyan-600 dark:border-dark-700 dark:bg-dark-850 dark:text-gray-100" />
                            </div>

                            <div className="space-y-3">
                                {visibleOrders.map((order, orderIndex) => {
                                    const eligible = (order.items || []).filter(item => item.eligible);
                                    const disabled = !selectable.some(value => value.orderId === order.orderId);
                                    const selected = selectedIds.includes(order.orderId);
                                    const waitingItem = (order.items || []).find(item => item.availableAt);
                                    return (
                                        <div key={order.orderId} className={`overflow-hidden rounded-xl border transition-colors ${
                                            selected ? 'border-cyan-400 bg-white shadow-sm dark:border-panda-cyan/40 dark:bg-dark-900'
                                                : 'border-gray-200 bg-gray-50/60 dark:border-dark-700 dark:bg-dark-850'
                                        }`}>
                                            <div className="flex items-start justify-between gap-3 p-4"><label className={`flex min-w-0 items-start gap-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                <input
                                                    type="checkbox"
                                                    className="mt-1 h-4 w-4 shrink-0 accent-cyan-700 dark:accent-panda-cyan"
                                                    aria-label={`Select Profile ${order.profileNumber == null ? '--' : String(order.profileNumber).padStart(2, '0')}`}
                                                    checked={selected}
                                                    disabled={disabled || loading}
                                                    onChange={() => toggleOrder(order.orderId)}
                                                />
                                                <span className="min-w-0">
                                                    <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                        {orderIndex + 1}. Profile {order.profileNumber == null
                                                            ? '--'
                                                            : String(order.profileNumber).padStart(2, '0')}
                                                        {' · '}{order.email}
                                                    </span>
                                                    <span className="block text-xs text-gray-600 dark:text-gray-400">
                                                        {order.orderNumber || 'No WOB number'} · Purchased {order.purchaseDateText || 'date missing'}
                                                    </span>
                                                </span>
                                            </label><span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${disabled ? 'bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-400' : 'bg-cyan-50 text-cyan-800 dark:bg-panda-cyan/10 dark:text-panda-cyan'}`}>
                                                {order.activeJob ? 'Running' : eligible.length ? `${eligible.length} available` : 'Waiting'}
                                            </span></div>
                                            {order.profileNumber == null && (
                                                <p className="px-4 pb-4 text-sm text-amber-800 dark:text-amber-200">
                                                    Add a profile number by editing this order.
                                                </p>
                                            )}
                                            {order.activeJob && (
                                                <p className="px-4 pb-4 text-sm text-amber-800 dark:text-amber-200">
                                                    This order already has an active automation job.
                                                </p>
                                            )}
                                            {!selected && !disabled && <p className="px-4 pb-4 pl-11 text-sm text-gray-500 dark:text-gray-400">Select this profile to choose its items.</p>}
                                            {disabled && !order.activeJob && <div className="px-4 pb-4">
                                                <p className="mb-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"><ClockIcon className="h-4 w-4" />
                                                    {waitingItem ? `${AUTOMATION_REASON_LABELS[waitingItem.reason] || label(waitingItem.reason)} · Available ${formatAutomationAvailableAt(waitingItem.availableAt)}` : 'No items are currently available for automation.'}
                                                </p>
                                                <AutomationItemSelector items={order.items || []} selectedIds={[]} onChange={() => {}} disabled />
                                            </div>}
                                            <AnimatePresence initial={false}>{selected && <motion.div key="items"
                                                initial={{height: 0, opacity: 0}} animate={{height: 'auto', opacity: 1}} exit={{height: 0, opacity: 0}}
                                                transition={{duration: 0.18}} className="overflow-hidden">
                                                <div className="border-t border-cyan-100 p-4 dark:border-panda-cyan/20">
                                                <AutomationItemSelector items={order.items || []} selectedIds={selectedItemsByOrder[order.orderId] || []}
                                                    disabled={loading} onChange={ids => setSelectedItemsByOrder(current => ({...current, [order.orderId]: ids}))} />
                                            </div></motion.div>}</AnimatePresence>
                                        </div>
                                    );
                                })}
                            </div>
                            {visibleOrders.length === 0 && <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500 dark:border-dark-700">{loading ? 'Loading profiles…' : preview.length ? 'No profiles match your search.' : 'No Process profiles to display.'}</p>}
                        </>
                    ) : view === 'history' ? (
                        <BatchAutomationHistory apiUrl={apiUrl} requestHeaders={requestHeaders}
                            page={historyPage} onPageChange={setHistoryPage} onSelect={openBatch} />
                    ) : detailLoading ? <p role="status" className="py-8 text-center text-sm text-gray-600 dark:text-gray-400">Loading batch results…</p>
                        : batch ? <BatchAutomationResults batch={batch} /> : null}
                </div>
            </DialogBody>

            <DialogFooter className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4 dark:border-dark-700">
                <div className="text-sm text-gray-700 dark:text-gray-300" aria-live="polite">
                    {showStart ? <><span className="font-semibold">{selectedOrders.length} {selectedOrders.length === 1 ? 'profile' : 'profiles'} · {selectedItemCount} {selectedItemCount === 1 ? 'item' : 'items'} selected</span>
                        {incompleteProfiles.length > 0 && <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">Choose items for {incompleteProfiles.map(order => `Profile ${String(order.profileNumber).padStart(2, '0')}`).join(', ')} to continue.</p>}</>
                        : <span>{view === 'history' ? 'Saved automation batches' : batch ? `${(batch.jobs || []).length} profiles in this batch` : 'Batch results'}</span>}
                </div><div className="flex flex-wrap items-center gap-2">
                <Button variant="text" color="black" onClick={onClose} className="mv-secondary-button">
                    <XMarkIcon className="h-4 w-4" /> Close
                </Button>
                {showStart && (
                    <Button
                        onClick={start}
                        disabled={!canStart}
                        className="mv-primary-button"
                    >
                        <BoltIcon className="h-4 w-4" /> {loading ? 'Starting...' : 'Start batch'}
                    </Button>
                )}
                {view === 'batch' && !detailLoading && batch && !isTerminal && (
                    <Button onClick={cancel} disabled={loading || batch.cancelRequested} className="mv-secondary-button">
                        <StopCircleIcon className="h-4 w-4" />
                        {batch.cancelRequested ? 'Cancelling...' : 'Cancel batch'}
                    </Button>
                )}
                </div>
            </DialogFooter>
        </Dialog>
    );
}
